import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "@/components/icons";

import { cn } from "@/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

type ToastViewportProps = React.ComponentPropsWithoutRef<
  typeof ToastPrimitives.Viewport
> & {
  mobilePosition?: "top" | "bottom";
};

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  ToastViewportProps
>(({ className, mobilePosition = "bottom", ...props }, ref) => {
  const [navbarHeight, setNavbarHeight] = React.useState(0);
  
  React.useEffect(() => {
    const measureNavbar = () => {
      const navbar = document.querySelector('.app-header');
      if (navbar) {
        const rect = navbar.getBoundingClientRect();
        const safeArea = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0px';
        const safeAreaValue = parseInt(safeArea) || 0;
        setNavbarHeight(rect.height + safeAreaValue);
      }
    };
    
    measureNavbar();
    globalThis.addEventListener('resize', measureNavbar);
    return () => globalThis.removeEventListener('resize', measureNavbar);
  }, []);
  
  // Position toasts either at top or bottom on mobile depending on prop.
  const baseClassTop = "fixed top-0 right-0 z-[10002] flex flex-col gap-2 max-h-screen w-full p-4 md:max-w-[420px]";
  const baseClassBottom = "fixed right-0 z-[10002] flex flex-col gap-2 max-h-screen w-full p-4 md:max-w-[420px]";

  const baseClass = mobilePosition === 'top' ? baseClassTop : baseClassBottom;
  
  const bottomStyle = mobilePosition === 'bottom' && navbarHeight > 0 
    ? { bottom: `${navbarHeight}px` } 
    : { bottom: '0' };

  return (
    <ToastPrimitives.Viewport
      ref={ref}
      style={mobilePosition === 'bottom' ? bottomStyle : undefined}
      className={cn(baseClass, className)}
      {...props}
    />
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all duration-150 sm:duration-250 data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full data-[state=open]:sm:slide-in-from-top-full",
  {
    variants: {
      variant: {
        default: "border bg-primary text-primary-foreground",
        destructive:
          "destructive group border-primary bg-primary text-primary-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & {
      onAction?: () => void;
    }
>(({ className, variant, onAction, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), onAction && "cursor-pointer hover:opacity-80 transition-opacity", className)}
      onClick={onAction}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-primary transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-foreground focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 text-primary-foreground/50 opacity-0 transition-opacity hover:text-primary-foreground focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-foreground group-hover:opacity-100",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
